<script setup>
import { computed, getCurrentInstance, ref, watch } from "vue";
import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
import adminUserService from "../../../services/user/adminUserService";
import ResumeModal from "../../../../user/components/modal/mypage/MyPageResumeModal.vue";
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import { useAlertStore } from "../../../../../stores/alertStore";

const { $alert, $confirm } = useAlertStore();

const loadingStore = usePageLoadingStore()
const { $gbNameKR, $yyyymmddHHmmss, $yyyymmdd, $yyyymm, $gb, $bizNum, $userGbKR } = getCurrentInstance().appContext.config.globalProperties;

const props = defineProps({
  user: {
    type: Object,
    default: () => ({}),
  },
});

const user = computed(() => props.user || {});

const userRecordDetail = ref(null);

const resumeModal = ref(null); // 이력서 모달

// 2. 기본 정보 for 이력서
const retrieveRecordDetail = () => {
  if (!user.value.userId) {
    return;
  }

  let paramObj = {
    userId: user.value.userId,
    propId: 0,
    hidden: 0,
  }
  adminUserService.retrieveUserMyPage(paramObj)
      .then( async (res) => {
        // console.log(res);
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          // console.log(result);
          result.introOpenYn = result.introOpenYn === 'Y';
          result.eduHistOpenYn = result.eduHistOpenYn === 'Y';
          result.portfolioOpenYn = result.portfolioOpenYn === 'Y';
          result.certForeignOpenYn = result.certForeignOpenYn === 'Y';
          result.jobHistOpenYn = result.jobHistOpenYn === 'Y';
          result.milOpenYn = result.milOpenYn === 'Y';
          userRecordDetail.value = result;
        } else {
        }
      }).catch( (res) =>  {
    console.error(res);
    $alert(res.response.data.result.message);
  });
}

// 3. 마스터 권한 YN - 기업
const onClickUpdateCorpMasterYn = async () => {
  const corpMasterYn = user.value.corpMasterYn === 'Y' ? 'N' : 'Y';

  let confirmMsg;
  let confirmBtn;

  if(corpMasterYn === 'Y') {
    confirmMsg = ["회원에게 마스터 권한을 부여할까요?", "마스터 회원은 일자리/장비 매칭을 할 수 있습니다."];
    confirmBtn = '권한 부여';
  } else {
    confirmMsg = ["마스터 권한을 취소 시킬까요?", "취소하더라도 매칭 내역 정보는 삭제되지 않습니다."];
    confirmBtn = '권한 취소';
  }
  const ok = await $confirm(confirmMsg, confirmBtn);

  if(ok) {
    let paramObj = {
      userId: user.value.userId,
      corpMasterYn,
    }
    loadingStore.loading = true;
    adminUserService.updateCorpMasterYn(paramObj)
        .then( async (res) => {
          loadingStore.loading = false;
          // console.log(res);
          if(res.data.code.toLowerCase() === 'success') {
            user.value = res.data.result;
          } else {
            $alert("마스터 권한 변경을 실패하였습니다.");
          }
        }).catch( (res) =>  {
      // console.log(res);
      loadingStore.loading = false;
      $alert(res.response.data.result.message);
    });
  }
}

// 4. 이력서 보기
const onClickResumeView = () => {
  resumeModal.value.init(false);
  resumeModal.value.retrieveRecordDetail(user.value.userId);
  resumeModal.value.show();
}

watch(
  () => user.value.userId,
  (userId) => {
    if (!userId) {
      return;
    }

    retrieveRecordDetail();
  },
  { immediate: true }
)
</script>
<template>
  <!-- s: 학생/취업준비생 -->
  <div v-if="user.userGb === 'STUDENT'">
    <div class="table-form">
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col />
        </colgroup>
        <tr>
          <th>회원유형</th>
          <td>{{ $userGbKR(user.userGb) }}</td>
          <th>회원코드</th>
          <td>{{ user.userCd }}</td>
        </tr>
        <tr>
          <th>아이디</th>
          <td>{{ user.userLoginId }}</td>
          <th>이름</th>
          <td>{{ user.userName }}</td>
        </tr>
        <tr>
          <th>핸드폰 번호</th>
          <td>{{ $phoneNum(user.phoneNum) }}</td>
          <th>이메일</th>
          <td>{{ user.email }}</td>
        </tr>
        <tr>
          <th>생년월일</th>
          <td>{{ $yyyymmdd(user.birthYmd) }}</td>
          <th>성별</th>
          <td>{{ user.sex === '01' ? '남자' : '여자' }}</td>
        </tr>
        <tr>
          <th>학교</th>
          <td>{{ user.school }}</td>
          <th>학력/상태</th>
          <td>{{ $gbNameKR('SCHOOL_GB', user.schoolGb) }}/{{ $gbNameKR('EDU_TENURE_GB', user.eduTenureGb) }}</td>
        </tr>
        <tr>
          <th>거주 지역</th>
          <td colspan="3">{{ user.residenceArea1 }} {{ user.residenceArea2 }}</td>
        </tr>
        <tr>
          <th>희망 직무</th>
          <td colspan="3">{{ user.desiredJob || '-' }}</td>
        </tr>
        <tr>
          <th>관심 카테고리</th>
          <td colspan="3">{{ user.interests || '-' }}</td>
        </tr>
        <tr>
          <th>이력서</th>
          <td colspan="3">
            <KSATextButton class="underline" text="이력서 보기" @click="onClickResumeView" />
          </td>
        </tr>
        <tr>
          <th>가입일</th>
          <td>{{ $yyyymmdd(user.regYmd) }}</td>
          <th>최근 접속일</th>
          <td>{{ $yyyymmdd(user.lastLoginDate) }}</td>
        </tr>
      </table>
    </div>
  </div>
  <!-- e: 학생/취업준비생 -->

  <!-- s:  재직자/기업 -->
  <div v-else-if="user.userGb === 'COMPANY' && user.corpMasterYn !== 'Y'">
    <div class="table-form">
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col width="30%"/>
        </colgroup>
        <tr>
          <th>회원유형</th>
          <td>{{ $userGbKR(user.userGb) }} <KSASecondaryButton class="sm-size ml-xs" text="마스터 권한 부여" @click="onClickUpdateCorpMasterYn" /></td>
          <th>회원코드</th>
          <td>{{ user.userCd }}</td>
        </tr>
        <tr>
          <th>아이디</th>
          <td>{{ user.userLoginId }}</td>
          <th>이름</th>
          <td>{{ user.userName }}</td>
        </tr>
        <tr>
          <th>핸드폰 번호</th>
          <td>{{ $phoneNum(user.phoneNum) }}</td>
          <th>이메일</th>
          <td>{{ user.email }}</td>
        </tr>
        <tr>
          <th>생년월일</th>
          <td>{{ $yyyymmdd(user.birthYmd) }}</td>
          <th>성별</th>
          <td>{{ user.sex === '01' ? '남자' : '여자' }}</td>
        </tr>
        <tr>
          <th>사업자등록번호</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizNum">{{ $bizNum(user.corporation.bizNum) }}</span>
            <span v-else>-</span>
          </td>
          <th>기업명</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpName">{{ user.corporation.corpName }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>거주 지역</th>
          <td colspan="3">{{ user.residenceArea1 }} {{ user.residenceArea2 }}</td>
        </tr>
        <tr>
          <th>본인 직무</th>
          <td colspan="3">{{ user.desiredJob || '-' }}</td>
        </tr>
        <tr>
          <th>관심 카테고리</th>
          <td colspan="3">{{ user.interests || '-' }}</td>
        </tr>
        <tr>
          <th>이력서</th>
          <td colspan="3">
            <KSATextButton class="underline" text="이력서 보기" @click="onClickResumeView" />
          </td>
        </tr>
        <tr>
          <th>가입일</th>
          <td>{{ $yyyymmdd(user.regYmd) }}</td>
          <th>최근 접속일</th>
          <td>{{ $yyyymmdd(user.lastLoginDate) }}</td>
        </tr>
      </table>
      <div class="content-title">
        <h4>기업 정보</h4>
      </div>
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col width="30%"/>
        </colgroup>
        <tr>
          <th>최초 등록일</th>
          <td>
            <span v-if="user.corporation && user.corporation.createDate">{{ $yyyymmddHHmmss(user.corporation.createDate) }}</span>
            <span v-else>-</span>
          </td>
          <th>최근 수정일</th>
          <td>
            <span v-if="user.corporation && user.corporation.modifyDate">{{ $yyyymmddHHmmss(user.corporation.modifyDate) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>로고이미지(CI)</th>
          <td colspan="3">
            <div v-if="user.corporation.ciImgDir" style="width: 160px; height: 160px;">
              <KSAImage :src="user.corporation.ciImgDir"/>
            </div>
          </td>
        </tr>
        <tr>
          <th>사업자등록번호</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizNum">{{ $bizNum(user.corporation.bizNum) }}</span><!-- 변수 사업자등록번호로 변경 필요 -->
            <span v-else>-</span>
          </td>
          <th>업종</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizType">{{ (user.corporation.bizType) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>기업명</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpName">{{ (user.corporation.corpName) }}</span>
            <span v-else>-</span>
          </td>
          <th>산업군</th>
          <td>
            <span v-if="user.corporation && user.corporation.industryGb">{{ $gbNameKR('INDUSTRY_GB', user.corporation.industryGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>대표자명</th>
          <td>
            <span v-if="user.corporation && user.corporation.ceoName">{{ user.corporation.ceoName }}</span>
            <span v-else>-</span>
          </td>
          <th>기업형태</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpGb">{{ $gbNameKR('CORP_GB', user.corporation.corpGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>창립연월</th>
          <td>
            <span v-if="user.corporation && user.corporation.foundYm">{{ $yyyymm(user.corporation.foundYm) }}</span>
            <span v-else>-</span>
          </td>
          <th>상장여부</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpPublicYn && user.corporation.corpPublicYn === 'Y'">상장</span>
            <span v-else-if="user.corporation && user.corporation.corpPublicYn && user.corporation.corpPublicYn === 'N'">비상장</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사원수</th>
          <td>
            <span v-if="user.corporation && user.corporation.employeeNumGb">{{ $gbNameKR('EMPLOYEE_NUM_GB', user.corporation.employeeNumGb) }}</span>
            <span v-else>-</span>
          </td>
          <th>매출액</th>
          <td>
            <span v-if="user.corporation && user.corporation.salesAmtGb">{{ $gbNameKR('SALES_AMT_GB', user.corporation.salesAmtGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사업장소재지</th>
          <td colspan="3">
            <span v-if="user.corporation && user.corporation.residenceArea1 && user.corporation.residenceArea2">{{ user.corporation.residenceArea1 }} {{ user.corporation.residenceArea2 }}</span>
            <span v-else-if="user.corporation && user.corporation.residenceArea1">{{ user.corporation.residenceArea1 }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>홈페이지 URL</th>
          <td colspan="3">
            <span v-if="user.corporation && user.corporation.homeUrl">{{ user.corporation.homeUrl }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사업자등록증</th>
          <td colspan="3">
            <KSATextButton v-if="user.corporation && user.corporation.bizLicenseDir" class="ml-xs" :text=user.corporation.bizLicenseName @click="$downloadForPrivate({fileName:user.corporation.bizLicenseName, fileDir: user.corporation.bizLicenseDir })" />
          </td>
        </tr>
        <tr>
          <th>기업 소개서</th>
          <td colspan="3">
            <KSATextButton v-if="user.corporation.corpIntroDir" class="ml-xs" :text=user.corporation.corpIntroName @click="$downloadForPrivate({fileName:user.corporation.corpIntroName, fileDir: user.corporation.corpIntroDir })" />
          </td>
        </tr>
      </table>
      <br>
      <table class="vertical word-break-all">
        <colgroup>
          <col width="200px" />
          <col />
        </colgroup>
        <tr>
          <th>기업소개</th>
          <td>
            <div class="text" v-html="user.corporation.corpIntro"></div>
          </td>
        </tr>
        <tr>
          <th>주요 연혁</th>
          <td>
            <div class="text" v-html="user.corporation.corpHistory"></div>
          </td>
        </tr>
      </table>
    </div>
  </div>
  <!-- e: 재직자/기업 -->

  <!-- s:  재직자/기업-마스터 -->
  <div v-else-if="user.userGb === 'COMPANY' && user.corpMasterYn === 'Y'">
    <div class="table-form">
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col width="30%"/>
        </colgroup>
        <tr>
          <th>회원유형</th>
          <td>{{ $userGbKR(user.userGb) }}-마스터 <KSASecondaryButton class="sm-size ml-xs" text="마스터 권한 취소" @click="onClickUpdateCorpMasterYn" /></td>
          <th>회원코드</th>
          <td>{{ user.userCd }}</td>
        </tr>
        <tr>
          <th>아이디</th>
          <td>{{ user.userLoginId }}</td>
          <th>이름</th>
          <td>{{ user.userName }}</td>
        </tr>
        <tr>
          <th>핸드폰 번호</th>
          <td>{{ $phoneNum(user.phoneNum) }}</td>
          <th>이메일</th>
          <td>{{ user.email }}</td>
        </tr>
        <tr>
          <th>생년월일</th>
          <td>{{ $yyyymmdd(user.birthYmd) }}</td>
          <th>성별</th>
          <td>{{ user.sex === '01' ? '남자' : '여자' }}</td>
        </tr>
        <tr>
          <th>사업자등록번호</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizNum">{{ $bizNum(user.corporation.bizNum) }}</span>
            <span v-else>-</span>
          </td>
          <th>기업명</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpName">{{ user.corporation.corpName }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>거주 지역</th>
          <td colspan="3">{{ user.residenceArea1 }} {{ user.residenceArea2 }}</td>
        </tr>
        <tr>
          <th>이력서</th>
          <td colspan="3">
            <KSATextButton class="underline" text="이력서 보기" @click="onClickResumeView" />
          </td>
        </tr>
        <tr>
          <th>가입일</th>
          <td>{{ $yyyymmdd(user.regYmd) }}</td>
          <th>최근 접속일</th>
          <td>{{ $yyyymmdd(user.lastLoginDate) }}</td>
        </tr>
      </table>
      <div class="content-title">
        <h4>기업 정보</h4>
      </div>
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col width="30%"/>
        </colgroup>
        <tr>
          <th>최초 등록일</th>
          <td>
            <span v-if="user.corporation && user.corporation.createDate">{{ $yyyymmddHHmmss(user.corporation.createDate) }}</span>
            <span v-else>-</span>
          </td>
          <th>최근 수정일</th>
          <td>
            <span v-if="user.corporation && user.corporation.modifyDate">{{ $yyyymmddHHmmss(user.corporation.modifyDate) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>로고이미지(CI)</th>
          <td colspan="3">
            <div v-if="user.corporation.ciImgDir" style="width: 160px; height: 160px;">
              <KSAImage :src="user.corporation.ciImgDir"/>
            </div>
          </td>
        </tr>
        <tr>
          <th>사업자등록번호</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizNum">{{ $bizNum(user.corporation.bizNum) }}</span><!-- 변수 사업자등록번호로 변경 필요 -->
            <span v-else>-</span>
          </td>
          <th>업종</th>
          <td>
            <span v-if="user.corporation && user.corporation.bizType">{{ (user.corporation.bizType) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>기업명</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpName">{{ (user.corporation.corpName) }}</span>
            <span v-else>-</span>
          </td>
          <th>산업군</th>
          <td>
            <span v-if="user.corporation && user.corporation.industryGb">{{ $gbNameKR('INDUSTRY_GB', user.corporation.industryGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>대표자명</th>
          <td>
            <span v-if="user.corporation && user.corporation.ceoName">{{ user.corporation.ceoName }}</span>
            <span v-else>-</span>
          </td>
          <th>기업형태</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpGb">{{ $gbNameKR('CORP_GB', user.corporation.corpGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>창립연월</th>
          <td>
            <span v-if="user.corporation && user.corporation.foundYm">{{ $yyyymm(user.corporation.foundYm) }}</span>
            <span v-else>-</span>
          </td>
          <th>상장여부</th>
          <td>
            <span v-if="user.corporation && user.corporation.corpPublicYn && user.corporation.corpPublicYn === 'Y'">상장</span>
            <span v-else-if="user.corporation && user.corporation.corpPublicYn && user.corporation.corpPublicYn === 'N'">비상장</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사원수</th>
          <td>
            <span v-if="user.corporation && user.corporation.employeeNumGb">{{ $gbNameKR('EMPLOYEE_NUM_GB', user.corporation.employeeNumGb) }}</span>
            <span v-else>-</span>
          </td>
          <th>매출액</th>
          <td>
            <span v-if="user.corporation && user.corporation.salesAmtGb">{{ $gbNameKR('SALES_AMT_GB', user.corporation.salesAmtGb) }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사업장소재지</th>
          <td colspan="3">
            <span v-if="user.corporation && user.corporation.residenceArea1 && user.corporation.residenceArea2">{{ user.corporation.residenceArea1 }} {{ user.corporation.residenceArea2 }}</span>
            <span v-else-if="user.corporation && user.corporation.residenceArea1">{{ user.corporation.residenceArea1 }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>홈페이지 URL</th>
          <td colspan="3">
            <span v-if="user.corporation && user.corporation.homeUrl">{{ user.corporation.homeUrl }}</span>
            <span v-else>-</span>
          </td>
        </tr>
        <tr>
          <th>사업자등록증</th>
          <td colspan="3">
            <KSATextButton v-if="user.corporation && user.corporation.bizLicenseDir" class="ml-xs" :text=user.corporation.bizLicenseName @click="$downloadForPrivate({fileName:user.corporation.bizLicenseName, fileDir: user.corporation.bizLicenseDir})" />
          </td>
        </tr>
        <tr>
          <th>기업 소개서</th>
          <td colspan="3">
            <KSATextButton v-if="user.corporation.corpIntroDir" class="ml-xs" :text=user.corporation.corpIntroName @click="$downloadForPrivate({fileName:user.corporation.corpIntroName, fileDir: user.corporation.corpIntroDir})" />
          </td>
        </tr>
      </table>
      <br>
      <table class="vertical word-break-all">
        <colgroup>
          <col width="200px" />
          <col />
        </colgroup>
        <tr>
          <th>기업소개</th>
          <td>
            <div class="text" v-html="user.corporation.corpIntro"></div>
          </td>
        </tr>
        <tr>
          <th>주요 연혁</th>
          <td>
            <div class="text" v-html="user.corporation.corpHistory"></div>
          </td>
        </tr>
      </table>
    </div>
  </div>
  <!-- e: 재직자/기업-마스터 -->

  <!-- s:  대학/연구실 -->
  <div v-else-if="user.userGb === 'SCHOOL'">
    <div class="table-form">
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col />
        </colgroup>
        <tr>
          <th>회원유형</th>
          <td>{{ $userGbKR(user.userGb) }}</td>
          <th>회원코드</th>
          <td>{{ user.userCd }}</td>
        </tr>
        <tr>
          <th>아이디</th>
          <td>{{ user.userLoginId }}</td>
          <th>담당자 이름</th>
          <td>{{ user.userName }}</td>
        </tr>
        <tr>
          <th>핸드폰 번호</th>
          <td>{{ $phoneNum(user.phoneNum) }}</td>
          <th>이메일</th>
          <td>{{ user.email }}</td>
        </tr>
        <tr>
          <th>생년월일</th>
          <td>{{ $yyyymmdd(user.birthYmd) }}</td>
          <th>성별</th>
          <td>{{ user.sex === '01' ? '남자' : '여자' }}</td>
        </tr>
        <tr>
          <th>거주 지역</th>
          <td colspan="3">{{ user.residenceArea1 }} {{ user.residenceArea2 }}</td>
        </tr>
        <tr>
          <th>학교</th>
          <td>{{ user.school }}</td>
          <th>학력/상태</th>
          <td>{{ $gbNameKR('SCHOOL_GB', user.schoolGb) }}/{{ $gbNameKR('EDU_TENURE_GB', user.eduTenureGb) }}</td>
        </tr>
        <tr>
          <th>교수님 이름</th>
          <td>{{ user.professorName}}</td>
          <th>연구실 이름</th>
          <td>{{ user.labName }}</td>
        </tr>
        <tr>
          <th>가입일</th>
          <td>{{ $yyyymmdd(user.regYmd) }}</td>
          <th>최근 접속일</th>
          <td>{{ $yyyymmdd(user.lastLoginDate) }}</td>
        </tr>
      </table>
    </div>
  </div>
  <!-- e: 대학/연구실 -->

  <!-- s:  강사/전문가 -->
  <div v-else-if="user.userGb === 'TEACHER'">
    <div class="table-form">
      <table class="vertical">
        <colgroup>
          <col width="200px" />
          <col />
          <col width="200px" />
          <col />
        </colgroup>
        <tr>
          <th>회원유형</th>
          <td>{{ $userGbKR(user.userGb) }}</td>
          <th>회원코드</th>
          <td>{{ user.userCd }}</td>
        </tr>
        <tr>
          <th>아이디</th>
          <td>{{ user.userLoginId }}</td>
          <th>이름</th>
          <td>{{ user.userName }}</td>
        </tr>
        <tr>
          <th>핸드폰 번호</th>
          <td>{{ $phoneNum(user.phoneNum) }}</td>
          <th>이메일</th>
          <td>{{ user.email }}</td>
        </tr>
        <tr>
          <th>생년월일</th>
          <td>{{ $yyyymmdd(user.birthYmd) }}</td>
          <th>성별</th>
          <td>{{ user.sex === '01' ? '남자' : '여자' }}</td>
        </tr>
        <tr>
          <th>교육 가능 과목</th>
          <td colspan="3">{{ user.ableClass || '-' }}</td>
        </tr>
        <tr>
          <th>전문 분야</th>
          <td colspan="3">{{ user.expertField || '-' }}</td>
        </tr>
      </table>
    </div>
  </div>
  <!-- e: 강사/전문가 -->
  <ResumeModal ref="resumeModal"/>
</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";
</style>
